import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MdOutlineTerrain, MdOutlineRefresh, MdOutlineFileDownload, MdOutlineHome, MdOutlineGavel, MdOutlineSchool,
  MdOutlinePeopleAlt, MdOutlineTrendingUp, MdOutlineAccountBalance, MdOutlineWorkOutline, MdOutlineErrorOutline,
  MdOutlineStorefront, MdOutlineSquareFoot, MdOutlineLandscape,
  MdOutlineFactCheck, MdOutlinePark, MdOutlineWarningAmber, MdOutlineForest, MdOutlineHomeWork, MdOutlineChatBubbleOutline,
} from 'react-icons/md';
import { selectPlacesLayers } from './placesSelectors';
import { evaluateLand, getIdToken } from '../atlas/atlasApi';
import LandEvaluationMap, { contourIntervalMeters, canopyFeatures, footprintFeatures, floodFeatures } from './LandEvaluationMap';
import MapSettingsBar, { DEFAULT_CANOPY_RADIUS_FT, DEFAULT_FOOTPRINTS_RADIUS_FT, DEFAULT_FLOOD_RADIUS_FT } from './MapSettingsBar';
import MapLegendStrip from './MapLegendStrip';
import LandSearchBar from './LandSearchBar';
import LocationSummary from './sections/LocationSummary';
import SectionStatusStrip, { PRIMARY_DATASETS, visibleDatasets } from './sections/SectionStatusStrip';
import PropertyCard from './sections/PropertyCard';
import DescriptionCard from './sections/DescriptionCard';
import DeedHistoryCard from './sections/DeedHistoryCard';
import SaleCompsCard from './sections/SaleCompsCard';
import SchoolsCard from './sections/SchoolsCard';
import NearbyPlacesCard from './sections/NearbyPlacesCard';
import SiteScreeningCard from './sections/SiteScreeningCard';
import TreeCanopyCard from './sections/TreeCanopyCard';
import BuildingFootprintsCard from './sections/BuildingFootprintsCard';
import DemographicsCard from './sections/DemographicsCard';
import HousingMarketCard from './sections/HousingMarketCard';
import CountyEconomyCard from './sections/CountyEconomyCard';
import EmploymentCard from './sections/EmploymentCard';
import { Pill } from './sections/ui';
import { fmtRelative, fmtDateTime, fmtCoords, displayAddress, safeArray, isNum } from './format';
import './LandEvaluation.css';

const SESSION_KEY = 'landEvaluation:lastQuery';
// Survives tool switches (the panel unmounts) without re-spending vendor calls.
const sessionCache = { result: null, elapsedMs: null, evaluatedAt: null, request: null };

/** Test hook: forget the cached result (the cache is module-level by design). */
export function resetLandEvaluationSession() {
  Object.assign(sessionCache, { result: null, elapsedMs: null, evaluatedAt: null, request: null });
}

const EXAMPLES = [
  { mode: 'address', value: '13 N Riverview Ave, Edison, NJ 08817', note: 'Vacant residential lot' },
  { mode: 'address', value: '1120 Wilson Ave, Abington, PA 19001', note: 'Single-family home' },
  { mode: 'coordinates', value: '30.2672, -97.7431', note: 'A coordinate pair' },
];

const DATASET_ICONS = {
  mls_property_details: <MdOutlineHome size={16} />,
  zoning: <MdOutlineSquareFoot size={16} />,
  parcel_terrain: <MdOutlineLandscape size={16} />,
  site_screening: <MdOutlineFactCheck size={16} />,
  tree_canopy: <MdOutlineForest size={16} />,
  building_footprints: <MdOutlineHomeWork size={16} />,
  county_deed_history: <MdOutlineGavel size={16} />,
  sale_comps: <MdOutlineStorefront size={16} />,
  schools: <MdOutlineSchool size={16} />,
  commercial_anchors: <MdOutlineStorefront size={16} />,
  public_places: <MdOutlinePark size={16} />,
  adverse_uses: <MdOutlineWarningAmber size={16} />,
  nearby_places: <MdOutlineStorefront size={16} />,
  demographics: <MdOutlinePeopleAlt size={16} />,
  housing_market: <MdOutlineTrendingUp size={16} />,
  county_economy: <MdOutlineAccountBalance size={16} />,
  employment_access: <MdOutlineWorkOutline size={16} />,
};

const DATASET_BLURBS = {
  mls_property_details: 'The public listing record: status and price, physical facts, valuation and carrying costs, description',
  zoning: 'Zoning district, permitted uses, height / density / coverage / setback controls and the parcel outline',
  parcel_terrain: 'USGS 1 m lidar elevation across the parcel: low and high points, relief, slope and aspect',
  site_screening: 'Factual parcel screens: registered facilities nearby, coastal barrier units, FEMA flood zones, wildfire hazard potential and landslide susceptibility',
  tree_canopy: 'Tree cover on the parcel from a 1 m canopy-height model: share, height classes, patches, and canopy in the ring around it',
  building_footprints: 'Building outlines on the parcel from Overture: count, footprint area, lot coverage, heights, structures crossing the line, and buildings around it',
  county_deed_history: 'County-recorded deeds, mortgages and filings, plus price history, tax history and the last sale',
  sale_comps: 'Nearby single-family sale listings: asking-price behavior and the new-construction premium by home size',
  schools: 'Schools whose attendance boundary contains the point, with ratings',
  commercial_anchors: 'Named brands near the point, ranked by editorial anchor importance',
  public_places: 'Named parks, playgrounds, pools, trails, libraries and other public places — mapped, nearest first, not scored',
  adverse_uses: 'Tagged adverse uses with a signed, distance-decayed screening score',
  nearby_places: 'Deprecated umbrella of the three place datasets, still carrying general amenity counts by category family',
  demographics: 'Age, income by tenure, household size and migration within your radius',
  housing_market: 'Up to ten years of monthly ZIP and county listing history with a peer benchmark',
  county_economy: 'Population, income, unemployment, establishments, credit and building permits',
  employment_access: 'Jobs located within 1 / 2 / 5 / 10 miles vs jobs held by residents, by sector',
};

function readSession() {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}

function writeSession(payload) {
  try { window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload)); } catch (e) { /* ignore */ }
}

function downloadJson(result) {
  const zip = result?.location?.canonical_address?.postal_code;
  const coords = result?.location?.coordinates;
  const stem = zip || (coords ? `${coords.latitude.toFixed(4)}_${coords.longitude.toFixed(4)}` : 'result');
  const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `land-evaluation-${stem}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function describeInput(input) {
  if (!input) return '';
  if (input.address) return input.address;
  if (input.coordinates) return fmtCoords(input.coordinates.latitude, input.coordinates.longitude, 5);
  return '';
}

export default function LandEvaluation({ onDiscussWithAtlas } = {}) {
  const saved = useMemo(() => readSession(), []);
  const [mode, setMode] = useState(saved?.mode || 'address');
  const [value, setValue] = useState(saved?.value || '');
  const [radius, setRadius] = useState(isNum(saved?.radius) ? saved.radius : 5);
  const [placesRadius, setPlacesRadius] = useState(isNum(saved?.placesRadius) ? saved.placesRadius : 3);
  const [compsRadius, setCompsRadius] = useState(isNum(saved?.compsRadius) ? saved.compsRadius : 2);
  const [canopyRadiusFt, setCanopyRadiusFt] = useState(isNum(saved?.canopyRadiusFt) ? saved.canopyRadiusFt : DEFAULT_CANOPY_RADIUS_FT);
  const [footprintsRadiusFt, setFootprintsRadiusFt] = useState(isNum(saved?.footprintsRadiusFt) ? saved.footprintsRadiusFt : DEFAULT_FOOTPRINTS_RADIUS_FT);
  // Neighbouring outlines are opt-in (`footprints_geojson`): the on-parcel
  // outlines always come back in `buildings[]`, so the default request stays small.
  const [showNeighbouringFootprints, setShowNeighbouringFootprints] = useState(saved?.showNeighbouringFootprints === true);
  // Flood zones on the lot always come back; the radius (0 = lot only) adds the zones around it.
  const [floodRadiusFt, setFloodRadiusFt] = useState(isNum(saved?.floodRadiusFt) ? saved.floodRadiusFt : DEFAULT_FLOOD_RADIUS_FT);
  const [status, setStatus] = useState(sessionCache.result ? 'success' : 'idle');
  const [result, setResult] = useState(sessionCache.result);
  const [error, setError] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(sessionCache.elapsedMs);
  const [evaluatedAt, setEvaluatedAt] = useState(sessionCache.evaluatedAt);
  const [lastRequest, setLastRequest] = useState(sessionCache.request);
  const [liveElapsed, setLiveElapsed] = useState(0);
  const [mapStyle, setMapStyle] = useState('satellite');
  const [pinMode, setPinMode] = useState(false);
  const [previewCenter, setPreviewCenter] = useState(null);
  const [focusPlace, setFocusPlace] = useState(null);
  const [layers, setLayers] = useState({ contours: true, terrain3d: false, terrainExaggeration: 1, parcel: true, zone: false, terrain: true, terrainMode: 'elevation', maxSlopePercent: 20, parcelContours: true, treeCanopy: true, footprints: true, flood: true, anchors: true, publicPlaces: true, externalities: true, comps: true, jobRadii: false });
  const [mapZoom, setMapZoom] = useState(null);
  const mapApiRef = useRef(null);
  const setLayer = useCallback((key, value) => setLayers((prev) => ({ ...prev, [key]: value })), []);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => { writeSession({ mode, value, radius, placesRadius, compsRadius, canopyRadiusFt, footprintsRadiusFt, showNeighbouringFootprints, floodRadiusFt }); }, [mode, value, radius, placesRadius, compsRadius, canopyRadiusFt, footprintsRadiusFt, showNeighbouringFootprints, floodRadiusFt]);
  // Warm the session token so terrain tiles requested before the first evaluation carry it.
  useEffect(() => { getIdToken().catch(() => {}); }, []);

  useEffect(() => {
    if (status !== 'loading') return undefined;
    const started = performance.now();
    setLiveElapsed(0);
    const timer = setInterval(() => setLiveElapsed(performance.now() - started), 200);
    return () => clearInterval(timer);
  }, [status]);

  useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

  const runEvaluation = useCallback(async (location, radiusMiles, placesRadiusMiles, compsRadiusMiles, canopyRadius, footprints = {}, floodRadius = 0) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus('loading');
    setError(null);
    setPreviewCenter(null);
    setFocusPlace(null);
    const request = { location, radius: radiusMiles, placesRadius: placesRadiusMiles, compsRadius: compsRadiusMiles, canopyRadiusFt: canopyRadius, footprintsRadiusFt: footprints.radiusFt, footprintsGeojson: Boolean(footprints.geojson), floodRadiusFt: floodRadius };
    setLastRequest(request);
    const started = performance.now();
    try {
      const data = await evaluateLand({
        address: location.address,
        coordinates: location.coordinates,
        demographicsRadiusMiles: radiusMiles,
        placesRadiusMiles,
        // The map draws the canopy, so the geometry is always requested; the
        // radius sets both the drawn ring and `summary.surround`.
        canopyGeojson: true,
        canopyRadiusFt: canopyRadius,
        // Footprints: the radius always goes (it sets `summary.surround`); the
        // neighbouring outlines only when asked for.
        footprintsRadiusFt: footprints.radiusFt,
        footprintsGeojson: Boolean(footprints.geojson),
        floodRadiusFt: floodRadius,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      const took = performance.now() - started;
      const at = new Date().toISOString();
      setResult(data);
      setElapsedMs(took);
      setEvaluatedAt(at);
      setStatus('success');
      Object.assign(sessionCache, { result: data, elapsedMs: took, evaluatedAt: at, request });
      if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      if (err?.name === 'AbortError' || controller.signal.aborted) return;
      setError(err);
      setStatus('error');
    }
  }, []);

  const footprintOptions = useMemo(() => ({ radiusFt: footprintsRadiusFt, geojson: showNeighbouringFootprints }), [footprintsRadiusFt, showNeighbouringFootprints]);

  const handleSubmit = useCallback((location) => {
    setPinMode(false);
    runEvaluation(location, radius, placesRadius, compsRadius, canopyRadiusFt, footprintOptions, floodRadiusFt);
  }, [radius, placesRadius, compsRadius, canopyRadiusFt, footprintOptions, floodRadiusFt, runEvaluation]);

  const handlePinDrop = useCallback((coordinates) => {
    setMode('coordinates');
    setValue(`${coordinates.latitude.toFixed(6)}, ${coordinates.longitude.toFixed(6)}`);
    runEvaluation({ coordinates }, radius, placesRadius, compsRadius, canopyRadiusFt, footprintOptions, floodRadiusFt);
  }, [radius, placesRadius, compsRadius, canopyRadiusFt, footprintOptions, floodRadiusFt, runEvaluation]);

  const rerun = useCallback(() => {
    if (lastRequest?.location) runEvaluation(lastRequest.location, radius, placesRadius, compsRadius, canopyRadiusFt, footprintOptions, floodRadiusFt);
  }, [lastRequest, radius, placesRadius, compsRadius, canopyRadiusFt, footprintOptions, floodRadiusFt, runEvaluation]);

  const applyExample = (example) => {
    setMode(example.mode);
    setValue(example.value);
    if (example.mode === 'coordinates') {
      const [lat, lng] = example.value.split(',').map((s) => Number(s.trim()));
      runEvaluation({ coordinates: { latitude: lat, longitude: lng } }, radius, placesRadius, compsRadius, canopyRadiusFt, footprintOptions, floodRadiusFt);
    } else {
      runEvaluation({ address: example.value }, radius, placesRadius, compsRadius, canopyRadiusFt, footprintOptions, floodRadiusFt);
    }
  };

  const center = result?.location?.coordinates && isNum(result.location.coordinates.latitude)
    ? { latitude: result.location.coordinates.latitude, longitude: result.location.coordinates.longitude }
    : null;
  const resultRadius = isNum(result?.demographics?.scope?.radius_miles) ? result.demographics.scope.radius_miles : (lastRequest?.radius || radius);
  const radiusDirty = Boolean(result) && radius !== resultRadius;
  // Every places consumer (card, map, legend availability) reads through this one view of the document.
  const placesLayers = useMemo(() => selectPlacesLayers(result), [result]);
  const resultPlacesRadius = isNum(placesLayers?.scope?.radius_miles) ? placesLayers.scope.radius_miles : (lastRequest?.placesRadius || placesRadius);
  const placesRadiusDirty = Boolean(result) && placesRadius !== resultPlacesRadius;
  // Tree canopy (2.2.0) is optional: `undefined` means an older backend that never
  // heard of it (render nothing at all); `null` with a status is an honest miss.
  const treeCanopy = result?.tree_canopy;
  const hasCanopyDataset = Boolean(result) && (treeCanopy !== undefined || Boolean(result?.section_status?.tree_canopy));
  const canopyGeometry = treeCanopy?.canopy || null;
  const resultCanopyRadius = isNum(treeCanopy?.scope?.radius_ft) ? treeCanopy.scope.radius_ft : (lastRequest?.canopyRadiusFt || canopyRadiusFt);
  const canopyRadiusDirty = Boolean(result) && canopyRadiusFt !== resultCanopyRadius;
  // Building footprints (2.2.0) follow the same optional-dataset rule as canopy.
  const buildingFootprints = result?.building_footprints;
  const hasFootprintsDataset = Boolean(result) && (buildingFootprints !== undefined || Boolean(result?.section_status?.building_footprints));
  const resultFootprintsRadius = isNum(buildingFootprints?.scope?.radius_ft) ? buildingFootprints.scope.radius_ft : (lastRequest?.footprintsRadiusFt || footprintsRadiusFt);
  const footprintsRadiusDirty = Boolean(result) && footprintsRadiusFt !== resultFootprintsRadius;
  // Turning the neighbouring outlines ON needs a re-run when the result was fetched
  // without them; turning them OFF just hides what was drawn.
  const resultHasNeighbouringFootprints = Boolean(buildingFootprints?.geojson) || Boolean(lastRequest?.footprintsGeojson);
  const neighbouringFootprintsDirty = Boolean(result) && showNeighbouringFootprints && !resultHasNeighbouringFootprints;
  // Flood zones (2.3.0) live under site_screening. `surround` echoes the radius the
  // result was fetched with; without it the result was lot-only (or predates the
  // option). No flood layer at all (older backend) never asks for a re-run.
  const floodLayer = result?.site_screening?.layers?.flood_zone;
  const floodData = floodLayer?.data || null;
  const resultFloodRadius = isNum(floodData?.surround?.radius_ft) ? floodData.surround.radius_ft : (isNum(lastRequest?.floodRadiusFt) ? lastRequest.floodRadiusFt : 0);
  const floodRadiusDirty = Boolean(result) && Boolean(floodLayer) && floodRadiusFt !== resultFloodRadius;
  const floodMapFeatures = floodFeatures(floodData?.geojson);
  const anyRadiusDirty = radiusDirty || placesRadiusDirty || canopyRadiusDirty || footprintsRadiusDirty || neighbouringFootprintsDirty || floodRadiusDirty;
  const sectionStatus = result?.section_status || null;
  const notOk = visibleDatasets(sectionStatus).filter((d) => !d.deprecated && sectionStatus[d.key]?.status && sectionStatus[d.key].status !== 'ok');
  const canonicalAddress = displayAddress(result?.location?.canonical_address?.formatted) || describeInput(lastRequest?.location);
  const loading = status === 'loading';
  const showStale = loading && Boolean(result);
  const parcelGeometry = result?.zoning?.parcel?.boundary_geojson || null;
  const zoneGeometry = result?.zoning?.zone_boundary_geojson || null;
  const terrainData = result?.parcel_terrain || null;
  const mapAvailability = { result: Boolean(center), parcel: Boolean(parcelGeometry), zone: Boolean(zoneGeometry), terrain: Boolean(terrainData?.samples?.length), places: Boolean(center && placesLayers?.hasAny), anchors: Boolean(center && placesLayers?.anchors?.length), publicPlaces: Boolean(center && placesLayers?.publicPlaces?.length), adverse: Boolean(center && placesLayers?.externalities?.length), comps: Boolean(result?.sale_comps?.points?.length), treeCanopy: canopyFeatures(canopyGeometry).length > 0, footprints: footprintFeatures(buildingFootprints).length > 0, footprintsSurround: footprintFeatures(buildingFootprints).some((f) => f.properties.location === 'surroundings' || f.properties.location === 'search_radius'), flood: floodMapFeatures.length > 0, floodSurround: floodMapFeatures.some((f) => f.properties.location === 'surroundings') };
  const elevationRange = isNum(terrainData?.stats?.min_ft) && isNum(terrainData?.stats?.max_ft) ? `${terrainData.stats.min_ft.toFixed(0)}–${terrainData.stats.max_ft.toFixed(0)} ft` : null;
  const exaggeration = Number(layers.terrainExaggeration) || 1;
  const terrainNote = terrainData ? (layers.terrain3d
    ? (exaggeration > 1 ? `3D terrain ×${exaggeration} vertical exaggeration (USGS 1 m) · buildings true scale` : '3D terrain at true scale (USGS 1 m)')
    : 'USGS 1 m lidar') : null;
  const contourInterval = contourIntervalMeters(mapZoom);
  const contourNote = contourInterval ? `Contours every ${contourInterval} m (${Math.round(contourInterval * 3.28084)} ft)` : 'Contours appear when zoomed in';

  return (
    <div className="le-root">
      <div className="le-toolbar">
        <LandSearchBar
          mode={mode}
          onModeChange={setMode}
          value={value}
          onValueChange={setValue}
          radius={radius}
          onRadiusChange={setRadius}
          placesRadius={placesRadius}
          onPlacesRadiusChange={setPlacesRadius}
          compsRadius={compsRadius}
          onCompsRadiusChange={setCompsRadius}
          onSubmit={handleSubmit}
          loading={loading}
          onPreview={setPreviewCenter}
          radiusDirty={radiusDirty}
          placesRadiusDirty={placesRadiusDirty}
        />
        {anyRadiusDirty && !loading ? (
          <div className="le-toolbar-hint">
            {radiusDirty ? `Demographics radius changed to ${radius} mi. ` : ''}{placesRadiusDirty ? `Places radius changed to ${placesRadius} mi. ` : ''}{canopyRadiusDirty ? `Canopy radius changed to ${canopyRadiusFt} ft. ` : ''}{footprintsRadiusDirty ? `Footprints radius changed to ${footprintsRadiusFt} ft. ` : ''}{neighbouringFootprintsDirty ? 'Neighbouring footprints requested. ' : ''}{floodRadiusDirty ? (floodRadiusFt ? `Flood zones around the lot changed to ${floodRadiusFt} ft. ` : 'Flood zones set to the lot only. ') : ''}
            <button type="button" className="le-link-btn" onClick={rerun}>Re-run</button> to refresh for the new {neighbouringFootprintsDirty && !(radiusDirty || placesRadiusDirty || canopyRadiusDirty || footprintsRadiusDirty || floodRadiusDirty) ? 'options' : 'radius'}.
          </div>
        ) : null}
      </div>

      {loading ? <div className="le-progress-bar" aria-hidden="true"><span /></div> : null}

      <div className="le-scroll" ref={scrollRef}>
        <div className="le-layout">
          <aside className="le-map-col">
            <MapSettingsBar
              mapStyle={mapStyle}
              onMapStyleChange={setMapStyle}
              pinMode={pinMode}
              onPinModeChange={setPinMode}
              hasResult={Boolean(center)}
              onRecenter={() => mapApiRef.current && mapApiRef.current.recenter()}
              onFitRadius={() => mapApiRef.current && mapApiRef.current.fitRadius()}
              layers={layers}
              available={mapAvailability}
              onLayerChange={setLayer}
              canopyRadiusFt={canopyRadiusFt}
              onCanopyRadiusChange={setCanopyRadiusFt}
              canopyRadiusDirty={canopyRadiusDirty}
              footprintsRadiusFt={footprintsRadiusFt}
              onFootprintsRadiusChange={setFootprintsRadiusFt}
              footprintsRadiusDirty={footprintsRadiusDirty}
              showNeighbouringFootprints={showNeighbouringFootprints}
              onShowNeighbouringFootprintsChange={setShowNeighbouringFootprints}
              neighbouringFootprintsDirty={neighbouringFootprintsDirty}
              floodRadiusFt={floodRadiusFt}
              onFloodRadiusChange={setFloodRadiusFt}
              floodRadiusDirty={floodRadiusDirty}
            />
            <LandEvaluationMap
              ref={mapApiRef}
              center={center}
              radiusMiles={resultRadius}
              address={canonicalAddress}
              previewCenter={previewCenter}
              pinMode={pinMode}
              onPinModeChange={setPinMode}
              onPinDrop={handlePinDrop}
              showJobRadii={layers.jobRadii}
              mapStyle={mapStyle}
              places={placesLayers}
              placesRadiusMiles={resultPlacesRadius}
              showAnchors={layers.anchors}
              showPublicPlaces={layers.publicPlaces}
              showExternalities={layers.externalities}
              focusPlace={focusPlace}
              parcel={parcelGeometry}
              zoneBoundary={zoneGeometry}
              showParcel={layers.parcel}
              showZone={layers.zone}
              showContours={layers.contours}
              terrain3d={layers.terrain3d}
              terrainExaggeration={layers.terrainExaggeration}
              onZoomChange={setMapZoom}
              terrain={terrainData}
              showTerrain={layers.terrain}
              showParcelContours={layers.parcelContours}
              terrainMode={layers.terrainMode}
              maxSlopePercent={layers.maxSlopePercent}
              comps={result?.sale_comps || null}
              showComps={layers.comps}
              treeCanopy={canopyGeometry}
              showTreeCanopy={layers.treeCanopy}
              buildingFootprints={buildingFootprints || null}
              showFootprints={layers.footprints}
              showNeighbouringFootprints={showNeighbouringFootprints}
              flood={floodData?.geojson || null}
              showFlood={layers.flood}
            />
            <MapLegendStrip
              hasResult={Boolean(center)}
              radiusMiles={resultRadius}
              placesRadiusMiles={resultPlacesRadius}
              layers={layers}
              available={mapAvailability}
              contourNote={contourNote}
              elevationRange={elevationRange}
              terrainNote={terrainNote}
              parcelContourIntervalFt={terrainData?.contours?.interval_ft}
              canopyRadiusFt={resultCanopyRadius}
              footprintsRadiusFt={resultFootprintsRadius}
              showNeighbouringFootprints={showNeighbouringFootprints}
              floodRadiusFt={resultFloodRadius}
            />
            {result?.location ? <LocationSummary location={result.location} /> : null}
          </aside>

          <main className={`le-content ${showStale ? 'le-stale' : ''}`}>
            {status === 'idle' && !result ? (
              <div className="le-empty">
                <div className="le-empty-hero">
                  <span className="le-empty-icon"><MdOutlineTerrain size={28} /></span>
                  <div>
                    <h2>Evaluate any parcel in the United States</h2>
                    <p>Enter an address or a coordinate pair — or drop a pin on the map — and get eleven datasets on the property, its zoning and parcel, its recorded history, and the places, market, people and jobs around it in one synchronous read.</p>
                  </div>
                </div>
                <div className="le-examples">
                  <span className="le-examples-label">Try one:</span>
                  {EXAMPLES.map((ex) => (
                    <button key={ex.value} type="button" className="le-example-btn" onClick={() => applyExample(ex)} title={ex.note}>
                      {ex.value}
                    </button>
                  ))}
                </div>
                <ul className="le-dataset-list">
                  {PRIMARY_DATASETS.map((d) => (
                    <li key={d.key}>
                      <span className="le-dataset-icon">{DATASET_ICONS[d.key]}</span>
                      <span><strong>{d.label}</strong><span className="le-dataset-blurb">{DATASET_BLURBS[d.key]}</span></span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {loading && !result ? (
              <div className="le-loading" role="status" aria-live="polite">
                <div className="le-loading-head">
                  <span className="le-spinner le-spinner-lg" aria-hidden="true" />
                  <div>
                    <h3>Evaluating {describeInput(lastRequest?.location)}</h3>
                    <p>Resolving the location, then fanning out every dataset. Usually about 10 seconds; the first request in a ZIP we have never indexed takes a little longer.</p>
                  </div>
                  <span className="le-loading-elapsed">{(liveElapsed / 1000).toFixed(1)}s</span>
                </div>
                <ul className="le-loading-list">
                  {PRIMARY_DATASETS.map((d, i) => (
                    <li key={d.key} style={{ animationDelay: `${i * 120}ms` }}>
                      <span className="le-dataset-icon">{DATASET_ICONS[d.key]}</span>
                      <span>{d.label}</span>
                      <span className="le-loading-dots"><i /><i /><i /></span>
                    </li>
                  ))}
                </ul>
                <div className="le-skeleton-stack">
                  <div className="le-skeleton" style={{ height: 120 }} />
                  <div className="le-skeleton" style={{ height: 220 }} />
                  <div className="le-skeleton" style={{ height: 180 }} />
                </div>
              </div>
            ) : null}

            {status === 'error' && error ? (
              <div className={`le-error ${result ? 'le-error-inline' : ''}`} role="alert">
                <MdOutlineErrorOutline size={22} />
                <div className="le-error-body">
                  <h3>{error.status === 422 ? 'Could not resolve that location' : error.status === 403 ? 'Land Evaluation is not enabled for this account' : error.status === 429 ? 'Slow down a little' : 'Evaluation failed'}</h3>
                  <p>{error.message}</p>
                  {safeArray(error.fieldErrors).length ? (
                    <ul className="le-warning-list">{error.fieldErrors.map((fe, i) => <li key={i}><code>{fe.loc}</code> — {fe.message}</li>)}</ul>
                  ) : null}
                  {error.status === 422 ? <p className="muted">Tip: include street number, street, city, state and ZIP — or switch to coordinates and drop a pin on the map.</p> : null}
                  {error.status === 403 ? <p className="muted">Reach out to the Real Torch team at admin@realtorch.ai to enable it.</p> : null}
                  {error.status !== 403 && lastRequest ? (
                    <button type="button" className="le-action-btn" onClick={rerun}><MdOutlineRefresh size={16} /> Try again</button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {result ? (
              <>
                <header className="le-result-head">
                  <div className="le-result-head-main">
                    <h2 className="le-result-title">{canonicalAddress}</h2>
                    <div className="le-result-meta">
                      <Pill tone={result.status === 'complete' ? 'good' : 'warning'} title={notOk.length ? `${notOk.map((d) => `${d.label}: ${sectionStatus[d.key].status}`).join(' · ')}` : 'Every dataset returned in full'}>
                        {result.status === 'complete' ? 'Complete' : `Partial · ${notOk.length} dataset${notOk.length === 1 ? '' : 's'} limited`}
                      </Pill>
                      <span title={fmtDateTime(evaluatedAt)}>Evaluated {fmtRelative(evaluatedAt)}{isNum(elapsedMs) ? ` in ${(elapsedMs / 1000).toFixed(1)}s` : ''}</span>
                      {result.request_id ? <span className="mono muted" title="Request id (quote this when reporting an issue)">{result.request_id}</span> : null}
                    </div>
                  </div>
                  <div className="le-result-actions">
                    <button type="button" className="le-action-btn" onClick={rerun} disabled={loading} title="Run the evaluation again"><MdOutlineRefresh size={16} /> Re-run</button>
                    <button type="button" className="le-action-btn" onClick={() => downloadJson(result)} title="Download the full document as JSON"><MdOutlineFileDownload size={16} /> JSON</button>
                    {typeof onDiscussWithAtlas === 'function' && result.request_id ? (
                      <button
                        type="button"
                        className="le-action-btn le-action-btn-primary"
                        onClick={() => onDiscussWithAtlas({ kind: 'land_evaluation', request_id: result.request_id, address: canonicalAddress })}
                        title="Open the chat with this evaluation attached. Atlas reads the stored result; no new lookups."
                      >
                        <MdOutlineChatBubbleOutline size={16} /> Discuss with Atlas
                      </button>
                    ) : null}
                  </div>
                </header>
                <SectionStatusStrip sectionStatus={sectionStatus} timing={result.timing} elapsedMs={elapsedMs} />
                {safeArray(result.warnings).length ? (
                  <div className="le-note le-note-warning"><MdOutlineErrorOutline size={16} /><div><ul className="le-warning-list">{result.warnings.map((w) => <li key={w}>{w}</li>)}</ul></div></div>
                ) : null}

                <PropertyCard data={result.mls_property_details} status={sectionStatus?.mls_property_details} zoning={result.zoning} zoningStatus={sectionStatus?.zoning} terrain={terrainData} terrainStatus={sectionStatus?.parcel_terrain} maxSlopePercent={layers.maxSlopePercent} />
                <DescriptionCard data={result.mls_property_details} status={sectionStatus?.mls_property_details} />
                <SiteScreeningCard data={result.site_screening} status={sectionStatus?.site_screening} />
                {hasCanopyDataset ? <TreeCanopyCard data={treeCanopy ?? null} status={sectionStatus?.tree_canopy} /> : null}
                {hasFootprintsDataset ? <BuildingFootprintsCard data={buildingFootprints ?? null} status={sectionStatus?.building_footprints} countyName={result.location?.canonical_address?.county_name} /> : null}
                <DeedHistoryCard data={result.county_deed_history} status={sectionStatus?.county_deed_history} />
                <SaleCompsCard data={result.sale_comps} status={sectionStatus?.sale_comps} />
                <SchoolsCard data={result.schools} status={sectionStatus?.schools} listingSchools={result.mls_property_details?.schools} />
                <NearbyPlacesCard data={placesLayers} status={placesLayers?.status} radiusMiles={resultPlacesRadius} onFocusPlace={setFocusPlace} />
                <DemographicsCard data={result.demographics} status={sectionStatus?.demographics} />
                <HousingMarketCard data={result.housing_market} status={sectionStatus?.housing_market} />
                <CountyEconomyCard data={result.county_economy} status={sectionStatus?.county_economy} />
                <EmploymentCard data={result.employment_access} status={sectionStatus?.employment_access} />

                <footer className="le-result-foot">
                  Property Context {result.schema_version ? `v${result.schema_version}` : ''} · generated {fmtDateTime(result.generated_at)} · facts stay labeled: recorded documents, listing-supplied facts and modeled estimates are never merged. Zero is a measured zero; a dash means unknown.
                </footer>
              </>
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
}
